import React, { useEffect, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const fmt = (v) => `AED ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tone = (v) => (v > 0 ? 'text-red-600' : v < 0 ? 'text-emerald-600' : 'text-gray-700');
const ALL = '__all';

export const ActualVsBudget = ({ departments }) => {
  const y = new Date().getFullYear();
  const [year, setYear] = useState(ALL);
  const [dept, setDept] = useState('');
  const [data, setData] = useState({ rows: [], summary: { count: 0, budget_total: 0, actual_total: 0, variance: 0 } });
  const params = { year: year === ALL ? '' : year, department: dept };

  useEffect(() => {
    api.get('/api/visa-costs/actual-vs-budget', { params }).then((r) => setData(r.data)).catch(() => toast({ title: 'Could not load', variant: 'destructive' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, dept]);

  const download = async () => {
    try {
      const r = await api.get('/api/visa-costs/actual-vs-budget', { params: { ...params, format: 'xlsx' }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: url, download: `visa_actual_vs_budget_${params.year || 'all'}.xlsx` }).click();
      URL.revokeObjectURL(url);
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
  };
  const s = data.summary;
  return (
    <div className="space-y-3" data-testid="vc-avb">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="h-9 w-32" data-testid="vc-avb-year"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All years</SelectItem>{[y - 2, y - 1, y, y + 1].map((v) => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={dept || ALL} onValueChange={(v) => setDept(v === ALL ? '' : v)}>
          <SelectTrigger className="h-9 w-44" data-testid="vc-avb-department"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All departments</SelectItem>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-4 text-sm">
          <span>{s.count} renewal(s)</span><span>Budget <b>{fmt(s.budget_total)}</b></span><span>Actual <b>{fmt(s.actual_total)}</b></span>
          <span className={tone(s.variance)} data-testid="vc-avb-variance">Variance <b>{fmt(s.variance)}</b></span>
          <Button size="sm" variant="outline" onClick={download} data-testid="vc-avb-download"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        </div>
      </div>
      <div className="border rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-gray-500"><tr>{['Employee', 'Renewal', 'Paid', 'Lines', 'Budget', 'Actual', 'Variance', 'Completed by'].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody className="divide-y">
            {data.rows.map((h) => (
              <tr key={h.id} className="align-top" data-testid={`vc-avb-row-${h.id}`}>
                <td className="px-3 py-2"><p className="font-medium">{h.name}</p><p className="text-xs text-gray-500">{h.employee_code} · {h.department}</p></td>
                <td className="px-3 py-2">{h.renewal_date || '—'}<p className="text-[10px] text-gray-400">next {h.new_renewal_date}</p></td>
                <td className="px-3 py-2">{h.paid_date || '—'}</td>
                <td className="px-3 py-2 text-xs">{h.lines.map((l, i) => <p key={i}>{l.description}: {l.budget.toFixed(2)} → <b className={tone(l.actual - l.budget)}>{l.actual.toFixed(2)}</b></p>)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmt(h.budget_total)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmt(h.actual_total)}</td>
                <td className={`px-3 py-2 whitespace-nowrap font-semibold ${tone(h.variance)}`}>{fmt(h.variance)}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{h.completed_by}</td>
              </tr>
            ))}
            {!data.rows.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500">No completed renewals for this selection. Record actuals and click “Complete renewal” from the Costs view.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
